/* eslint-disable react/prop-types */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRolePermissions } from '@app/shared/hooks/useRolePermissions';
import { Row, Col, Card } from 'react-bootstrap';
import Typography from '@mui/material/Typography';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import {
  GovernanceGateChart,
  SensitiveAttributesFrequencyPanel,
  ComplianceVsAuditChart,
  VerdictBarChart
} from './BiasRecharts';
import type { GovernanceGateDataItem, GovernanceGateOverlay } from './BiasRecharts';
import { BIAS_GRAPH_BLUE, BIAS_GRAPH_GREEN, BIAS_GRAPH_RED } from './biasLlmChartPalette';
import { pickBestAggregateSummary } from '../../../utils/aggregateSummaryPick';
import { pickLatestCollectionRun } from '../../../utils/collectionRuns';
import { metricScalarFromEntry } from './biasSummaryReference.utils';
import {
  isNonEmptyRecord,
  mergeBiasAggregateSummaries,
  resolveBiasAggregateSummary
} from './biasAggregateSummary.utils';
import { buildSensitiveAttributeCounts, sensitiveMentionScopeLabel } from './biasSensitiveAttributeCounts.utils';

/**
 * Shared section that renders the Bias LLM plots (same as Bias Tab).
 * Used in Review 360 tab and anywhere else that needs these charts.
 * Data is derived from projectResults (Redux) aggregate_summary.
 * When `customSummary` is provided (e.g. from the 360-analysis API), it takes priority.
 */
interface Props {
  /** Pre-aggregated bias summary from the 360-analysis API. Takes priority over Redux data. */
  customSummary?: Record<string, any>;
}

const BiasLLMPlotsSection: React.FC<Props> = ({ customSummary }) => {
  const projectResults = useSelector(selectProjectResults);
  const { isDeveloper } = useRolePermissions();
  const softPalette = isDeveloper;

  const reduxAggregateSummary = useMemo(() => {
    if (!projectResults || typeof projectResults !== 'object') {
      return undefined;
    }
    const fromBiasArray = (arr: unknown) => {
      const item = pickLatestCollectionRun(arr as { aggregate_summary?: unknown }[] | null | undefined);
      return resolveBiasAggregateSummary(item);
    };
    const fromBiasNode = (node: unknown) => resolveBiasAggregateSummary(node);

    const direct = pickBestAggregateSummary([
      fromBiasNode(projectResults?.results?.aggregate_summary),
      fromBiasNode(projectResults?.results?.bias),
      fromBiasArray(projectResults?.results?.bias),
      fromBiasNode(projectResults?.results?.bias_llm),
      fromBiasArray(projectResults?.results?.bias_llm),
      fromBiasNode(projectResults?.feature_results?.aggregate_summary),
      fromBiasNode(projectResults?.feature_results?.bias),
      fromBiasArray(projectResults?.feature_results?.bias),
      fromBiasNode(projectResults?.feature_results?.bias_llm),
      fromBiasArray(projectResults?.feature_results?.bias_llm),
      fromBiasNode(projectResults?.collection_results?.aggregate_summary),
      fromBiasNode(projectResults?.collection_results?.bias),
      fromBiasArray(projectResults?.collection_results?.bias),
      fromBiasNode(projectResults?.collection_results?.bias_llm),
      fromBiasArray(projectResults?.collection_results?.bias_llm)
    ]);
    if (direct) return direct;

    // Resolve when bias is under alternate keys or is an array (e.g. collection_results.bias = [{ aggregate_summary }])
    const candidateSources = [
      projectResults?.results,
      projectResults?.feature_results,
      projectResults?.collection_results
    ].filter(Boolean) as Record<string, unknown>[];

    const llmKeys = ['bias', 'bias_llm', 'LLMBiasDetection', 'llmBiasDetection', 'llm_bias_detection', 'BiasLLM'];

    for (const source of candidateSources) {
      for (const key of llmKeys) {
        const bucket = source?.[key];
        if (Array.isArray(bucket) && bucket.length > 0) {
          const latest = pickLatestCollectionRun(bucket);
          const resolved = resolveBiasAggregateSummary(latest);
          if (resolved) return resolved;
        } else if (bucket && typeof bucket === 'object') {
          const resolved = resolveBiasAggregateSummary(bucket);
          if (resolved) return resolved;
        }
      }
      if (source && typeof source === 'object' && 'aggregate_summary' in source) {
        const resolved = resolveBiasAggregateSummary(source);
        if (resolved) return resolved;
      }
    }
    return undefined;
  }, [projectResults]);

  const globalAggregateSummary: Record<string, any> | undefined = useMemo(
    () =>
      mergeBiasAggregateSummaries(isNonEmptyRecord(customSummary) ? customSummary : undefined, reduxAggregateSummary),
    [customSummary, reduxAggregateSummary]
  );

  const verdictData = useMemo(() => {
    const summary = globalAggregateSummary;
    const verdictDistribution = summary?.verdict_distribution;
    if (!verdictDistribution || typeof verdictDistribution !== 'object') {
      return null;
    }
    const verdicts: { key: 'APPROVE' | 'REVIEW' | 'REJECT'; color: string }[] = isDeveloper
      ? [
          { key: 'APPROVE', color: BIAS_GRAPH_BLUE },
          { key: 'REVIEW', color: BIAS_GRAPH_BLUE },
          { key: 'REJECT', color: BIAS_GRAPH_BLUE }
        ]
      : [
          { key: 'APPROVE', color: BIAS_GRAPH_GREEN },
          { key: 'REVIEW', color: '#f59e0b' },
          { key: 'REJECT', color: BIAS_GRAPH_RED }
        ];
    return verdicts.map(({ key, color }) => ({
      name: key,
      value: verdictDistribution[key] ?? 0,
      color
    }));
  }, [globalAggregateSummary, isDeveloper]);

  const complianceVsAuditData = useMemo(() => {
    const summary = globalAggregateSummary;
    const avgMetrics = summary?.avg_metrics;
    const auditMetrics = summary?.audit_avg_metrics || summary?.audited_avg_metrics;
    if (!avgMetrics || typeof avgMetrics !== 'object') return null;
    const metricKeys = ['WEAT_Avg', 'StereoSet', 'CrowsPairs', 'Toxicity'] as const;
    const categories = {
      WEAT_Avg: 'Association Skew',
      StereoSet: 'Stereotype Score',
      CrowsPairs: 'Group Favoritism',
      Toxicity: 'Hostility Score'
    } as const;
    const hasAuditData = auditMetrics && typeof auditMetrics === 'object';
    return metricKeys.map(key => ({
      metric: categories[key],
      compliance: Number(Number(metricScalarFromEntry(avgMetrics[key]) ?? 0).toFixed(3)),
      audit: hasAuditData ? Number(Number(metricScalarFromEntry(auditMetrics?.[key]) ?? 0).toFixed(3)) : null
    }));
  }, [globalAggregateSummary]);

  const governanceGateData = useMemo((): GovernanceGateDataItem[] | null => {
    const summary = globalAggregateSummary;
    const funnel = summary?.funnel;
    if (!funnel || typeof funnel !== 'object') return null;
    return [
      {
        label: 'Sensitive Detected',
        value: funnel.sensitive_detected?.count ?? 0,
        color: BIAS_GRAPH_BLUE
      },
      {
        label: 'Whitelisted (Policy + Reviewer)',
        value: funnel.whitelisted_only?.count ?? 0,
        color: isDeveloper ? BIAS_GRAPH_BLUE : BIAS_GRAPH_GREEN
      },
      {
        label: 'Residual Review Required',
        value: funnel.residual_review_required?.count ?? 0,
        color: isDeveloper ? BIAS_GRAPH_BLUE : BIAS_GRAPH_RED
      }
    ];
  }, [globalAggregateSummary, isDeveloper]);

  const sensitiveAttributeCounts = useMemo(
    () => buildSensitiveAttributeCounts(globalAggregateSummary),
    [globalAggregateSummary]
  );

  const governanceGateOverlay = useMemo((): GovernanceGateOverlay | null => {
    const summary = globalAggregateSummary;
    const density = summary?.sensitive_density;
    const densityTotal =
      density && typeof density === 'object'
        ? Number((density as Record<string, unknown>).total_sensitive_mentions ?? NaN)
        : NaN;
    const allSensitiveMentions = Number.isFinite(densityTotal) ? densityTotal : null;
    const attributeTotal = sensitiveAttributeCounts?.totalMentions ?? null;
    const totalSensitiveMentions = attributeTotal ?? allSensitiveMentions ?? 0;

    if (!density || typeof density !== 'object') {
      if (attributeTotal == null) return null;
      return {
        totalSensitiveMentions,
        avgPerOutput: 0,
        minPerOutput: 0,
        maxPerOutput: 0,
        allSensitiveMentions,
        mentionScopeLabel: sensitiveAttributeCounts
          ? sensitiveMentionScopeLabel(sensitiveAttributeCounts.scope)
          : 'Sensitive mentions'
      };
    }

    return {
      totalSensitiveMentions,
      avgPerOutput: Number((density as Record<string, unknown>).avg_per_output ?? 0),
      minPerOutput: Number((density as Record<string, unknown>).min_per_output ?? 0),
      maxPerOutput: Number((density as Record<string, unknown>).max_per_output ?? 0),
      allSensitiveMentions,
      mentionScopeLabel: sensitiveAttributeCounts
        ? sensitiveMentionScopeLabel(sensitiveAttributeCounts.scope)
        : 'Sensitive mentions'
    };
  }, [globalAggregateSummary, sensitiveAttributeCounts]);

  const sensitiveAttributesTriggeredData = sensitiveAttributeCounts?.items ?? null;

  const sensitiveAttributesLede =
    sensitiveAttributeCounts?.scope === 'non_whitelisted'
      ? 'Non-whitelisted attribute hits — prompt, metadata, and output combined.'
      : 'How often each attribute showed up — prompt, metadata, and output combined.';

  const sensitiveAttributesTotalLabel =
    sensitiveAttributeCounts?.scope === 'non_whitelisted' ? 'Non-whitelisted mentions' : 'Total mentions';

  const microeaiBiasScore = useMemo(() => {
    const summary = globalAggregateSummary;
    const score = summary?.microeai_bias_score;
    return typeof score === 'number' && !Number.isNaN(score) ? score : null;
  }, [globalAggregateSummary]);

  // Show charts whenever we have an aggregate summary; each chart shows its own empty state if needed
  if (!globalAggregateSummary) {
    return (
      <Typography variant="body2" color="text.secondary">
        No bias LLM data available for this project. Run bias analysis for this project to see charts here.
      </Typography>
    );
  }

  return (
    <Row className="g-4">
      <Col xs={12}>
        <Card className="shadow-sm border-0 overflow-visible" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#334155',
                fontSize: '1rem'
              }}
            >
              Entity-Level Bias Status (Governance Gate)
            </Typography>
            <GovernanceGateChart
              data={governanceGateData}
              overlay={governanceGateOverlay}
              height={320}
              emptyMessage="No governance gate data available."
            />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12}>
        <Card className="shadow-sm border-0 overflow-visible" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <SensitiveAttributesFrequencyPanel
              title="Sensitive attributes"
              data={sensitiveAttributesTriggeredData}
              totalMentions={sensitiveAttributeCounts?.totalMentions}
              totalLabel={sensitiveAttributesTotalLabel}
              lede={sensitiveAttributesLede}
              maxHeight={340}
              emptyMessage="No sensitive attributes detected."
              softPalette={softPalette}
              barsAllBlue={isDeveloper}
            />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <Card className="shadow-sm border-0 overflow-visible h-100" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#334155',
                fontSize: '1rem'
              }}
            >
              Bias Metrics (Compliance vs Audit)
            </Typography>
            <ComplianceVsAuditChart
              data={complianceVsAuditData}
              microeaiBiasScore={microeaiBiasScore}
              height={320}
              emptyMessage="No compliance vs audit metrics available."
              softPalette={softPalette}
              barsAllBlue={isDeveloper}
            />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} lg={6}>
        <Card className="shadow-sm border-0 overflow-visible h-100" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4 d-flex flex-column">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#334155',
                fontSize: '1rem'
              }}
            >
              Final Compliance Verdict
            </Typography>
            <div className="flex-grow-1">
              <VerdictBarChart
                data={verdictData}
                height={320}
                showRejectNote
                emptyMessage="No verdict distribution data available."
              />
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default BiasLLMPlotsSection;
